import { supabase } from '@/core/database/supabase';
import type { ConstructionSchedule, ScheduleFilters } from '../types/construction-schedules.types';

export const ConstructionSchedulesService = {
  async getSchedules(companyId: string, filters: ScheduleFilters = {}): Promise<{ data: ConstructionSchedule[], total: number }> {
    let query = supabase
      .from('construction_schedules')
      .select(`
        *,
        project:projects(id, name),
        phase:project_phases(id, name),
        zone:project_zones(id, name),
        team:teams(id, name, code),
        foreman:user_profiles!assigned_foreman_id(id, full_name)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('work_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.teamId) query = query.eq('assigned_team_id', filters.teamId);
    if (filters.date) {
      query = query.lte('scheduled_start_date', filters.date).gte('scheduled_end_date', filters.date);
    }
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,schedule_code.ilike.%${filters.search}%`);
    }

    query = query.order('scheduled_start_date', { ascending: true });

    const { data, error, count } = await query;
    if (error) throw error;
    
    return {
      data: (data || []) as ConstructionSchedule[],
      total: count || 0
    };
  },

  async getScheduleDetail(id: string): Promise<ConstructionSchedule> {
    const { data, error } = await supabase
      .from('construction_schedules')
      .select(`
        *,
        project:projects(id, name),
        phase:project_phases(id, name),
        zone:project_zones(id, name),
        team:teams(id, name, code),
        foreman:user_profiles!assigned_foreman_id(id, full_name)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as ConstructionSchedule;
  },

  async createSchedule(data: Partial<ConstructionSchedule>): Promise<ConstructionSchedule> {
    const { data: result, error } = await supabase
      .from('construction_schedules')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionSchedule;
  },

  async updateSchedule(id: string, updates: Partial<ConstructionSchedule>): Promise<void> {
    const { error } = await supabase
      .from('construction_schedules')
      .update(updates)
      .eq('id', id);

    if (error) throw error;
  }
};

